import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Award, CheckCircle, FileText, Headphones, Settings, Upload, UserPlus, Users, Video } from 'react-feather';

import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  SearchInput,
  SegmentedControl,
  Select,
  StatusBadge,
  Table,
  Toolbar,
} from '../../../ui';
import {
  BoardColumn,
  Card as CandidateCard,
  DrawerTab,
  FUNNEL,
  Filters,
  Genre,
  HIRING_ENABLED,
  HiringMeta,
  LeadSource,
  PARKED,
  SOURCE_LABELS,
  STAGE_LABELS,
  STAGE_STATUS_LABELS,
  Stage,
  Stats,
  ago,
  canMove,
  errorText,
  getBoard,
  getGenres,
  getMeta,
  getStats,
  initials,
  latestScore,
  listCandidates,
  nextAction,
  waited,
} from '../../../TeacherHiring/hiringApi';
import AddCandidateDialog from './AddCandidateDialog';
import CandidateDrawer from './CandidateDrawer';
import CopyLinkButton from './CopyLinkButton';
import ImportCandidatesDialog from './ImportCandidatesDialog';
import MoveDialog from './MoveDialog';
import './teacherHiring.scss';

type View = 'table' | 'board';
const PAGE_SIZE = 25;

/** The latest AI or interview score, as the coloured box the table and cards share. */
export const ScoreBox: React.FC<{ card: Pick<CandidateCard, 'screening' | 'interview'> }> = ({ card }) => {
  const score = latestScore(card);
  if (score == null) return <span className="th-scorebox th-scorebox--none">–</span>;
  const tone = score >= 85 ? 'high' : score >= 70 ? 'mid' : 'low';
  return <span className={`th-scorebox th-scorebox--${tone}`} title="Latest AI or interview score">{score}</span>;
};

const KPI_ICONS = [FileText, Headphones, Video, Users, Award, CheckCircle];

const Kpis: React.FC<{ stats: Stats }> = ({ stats }) => (
  <div className="th-kpis">
    {stats.funnel.map((step, i) => {
      const Icon = KPI_ICONS[i] ?? FileText;
      const label = i === 0 ? 'Total leads' : step.label === 'AI Screening' ? 'AI screened' : step.label === 'Video Interview' ? 'Video interviews' : step.label === 'Academic Interview' ? 'Academic interviews' : step.label === 'Training' ? 'In training' : 'Onboarded';
      const sub =
        i === 0
          ? stats.leadsChangePct == null
            ? stats.days ? `last ${stats.days} days` : 'all time'
            : `${stats.leadsChangePct >= 0 ? '↑' : '↓'} ${Math.abs(stats.leadsChangePct)}% vs previous ${stats.days} days`
          : `${step.pctOfPrevious}% of ${stats.funnel[i - 1].label.toLowerCase()}`;
      return (
        <div key={step.stage} className={`th-kpi th-kpi--${i + 1}`}>
          <Icon size={22} className="th-kpi__icon" aria-hidden />
          <div>
            <div className="th-kpi__value ds-num">{step.count.toLocaleString('en-IN')}</div>
            <div className="th-kpi__label">{label}</div>
          </div>
          <div className="th-kpi__sub">{sub}</div>
        </div>
      );
    })}
  </div>
);

const Pipeline: React.FC<{ stats: Stats; onPick: (stage: Stage) => void }> = ({ stats, onPick }) => (
  <ol className="th-pipeline">
    {stats.funnel.map((step, i) => (
      <li key={step.stage} className={`th-pipeline__step th-pipeline__step--${i + 1}`}>
        <button type="button" className="th-pipeline__dot" onClick={() => onPick(step.stage)} aria-label={`Show ${step.label}`}>
          {i + 1}
        </button>
        <div className="th-pipeline__label">{step.label}</div>
        <div className="th-pipeline__count ds-text-subtle ds-num">
          {step.count.toLocaleString('en-IN')}{i > 0 && ` (${step.pctOfLeads}%)`}
        </div>
      </li>
    ))}
  </ol>
);

const TeacherHiringBoard: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view: View = params.get('view') === 'board' ? 'board' : 'table';
  const filters: Filters & { stage?: string } = {
    q: params.get('q') ?? '',
    genre: params.get('genre') ?? '',
    source: params.get('source') ?? '',
    owner: params.get('owner') ?? '',
    stage: params.get('stage') ?? '',
  };
  const [search, setSearch] = useState(filters.q ?? '');
  const [days, setDays] = useState(30);

  const [genres, setGenres] = useState<Genre[]>([]);
  const [meta, setMeta] = useState<HiringMeta | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [columns, setColumns] = useState<BoardColumn[] | null>(null);
  const [list, setList] = useState<{ total: number; page: number; pageSize: number; candidates: CandidateCard[] } | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [open, setOpen] = useState<{ id: string; tab: DrawerTab } | null>(null);
  const [move, setMove] = useState<{ card: CandidateCard; to: Stage } | null>(null);
  const [dragging, setDragging] = useState<CandidateCard | null>(null);
  const [dropTarget, setDropTarget] = useState<Stage | null>(null);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  // Debounced search, so typing a name doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== (params.get('q') ?? '')) setFilter('q', search.trim());
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const filterKey = params.toString();
  const load = useCallback(async () => {
    if (!HIRING_ENABLED) return;
    setLoading(true);
    setError(null);
    try {
      if (view === 'board') setColumns((await getBoard(filters)).columns);
      else setList(await listCandidates({ ...filters, stage: filters.stage || undefined, page, pageSize: PAGE_SIZE, sort: 'stageChangedAt' }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, view, page]);

  const loadStats = useCallback(() => {
    if (HIRING_ENABLED) getStats(days).then(setStats).catch(() => setStats(null));
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadStats();
  }, [loadStats]);
  useEffect(() => {
    if (!HIRING_ENABLED) return;
    getGenres().then(setGenres).catch(() => setGenres([]));
    getMeta().then(setMeta).catch(() => setMeta(null));
  }, []);

  const refresh = () => {
    load();
    loadStats();
  };

  const genreName = useMemo(() => new Map(genres.map((g) => [g.id, g.name])), [genres]);
  const genresOf = (c: CandidateCard) => c.genres.map((g) => genreName.get(g) ?? g).join(', ') || '—';

  const act = (card: CandidateCard) => {
    const action = nextAction(card);
    if (action.move) setMove({ card, to: action.move });
    else setOpen({ id: card.id, tab: action.tab ?? 'overview' });
  };

  if (!HIRING_ENABLED) {
    return (
      <Page>
        <PageHeader eyebrow="Academics" title="Teacher Recruitment" />
        <Callout tone="warning" title="Not available in this environment yet">
          The Teacher Recruitment service isn't configured here (REACT_APP_TEACHER_HIRING_BASEURL).
        </Callout>
      </Page>
    );
  }

  // --- Board view ------------------------------------------------------------

  const renderCard = (card: CandidateCard) => (
    <li
      key={card.id}
      className={`th-card${dragging?.id === card.id ? ' th-card--dragging' : ''}`}
      draggable
      onDragStart={(e) => {
        setDragging(card);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => {
        setDragging(null);
        setDropTarget(null);
      }}
    >
      <button type="button" className="th-card__name ds-link" onClick={() => setOpen({ id: card.id, tab: 'overview' })}>
        {card.name}
      </button>
      <div className="th-card__meta ds-text-subtle">{[genresOf(card), card.city].filter(Boolean).join(' · ')}</div>
      <div className="th-card__chips">
        {card.stageStatus !== 'PENDING' && <StatusBadge status={card.stageStatus} label={STAGE_STATUS_LABELS[card.stageStatus]} />}
        {latestScore(card) != null && <ScoreBox card={card} />}
      </div>
      <div className="th-card__foot ds-text-subtle">
        <span>{SOURCE_LABELS[card.source]}</span>
        <span title="Time in this stage">{waited(card.stageChangedAt)}</span>
      </div>
      {card.rejectionReason && card.stage === 'REJECTED' && <div className="th-card__reason ds-text-subtle">{card.rejectionReason}</div>}
    </li>
  );

  const renderColumn = (column: BoardColumn, parked = false) => {
    const droppable = dragging ? canMove(dragging.stage, column.stage) : false;
    return (
      <section
        key={column.stage}
        className={['th-column', parked && 'th-column--parked', dragging && (droppable ? 'th-column--droppable' : 'th-column--blocked'), dropTarget === column.stage && 'th-column--over']
          .filter(Boolean)
          .join(' ')}
        aria-label={STAGE_LABELS[column.stage]}
        onDragOver={(e) => {
          if (!droppable) return;
          e.preventDefault();
          setDropTarget(column.stage);
        }}
        onDragLeave={() => setDropTarget((t) => (t === column.stage ? null : t))}
        onDrop={(e) => {
          e.preventDefault();
          setDropTarget(null);
          if (dragging && canMove(dragging.stage, column.stage)) setMove({ card: dragging, to: column.stage });
          setDragging(null);
        }}
      >
        <header className="th-column__head">
          <span className="th-column__title">{STAGE_LABELS[column.stage]}</span>
          <span className="th-column__count ds-num">{column.count}</span>
          {column.awaitingReview > 0 && <Badge tone="accent">{column.awaitingReview} to review</Badge>}
        </header>
        <ul className="th-column__cards">
          {column.candidates.map(renderCard)}
          {column.candidates.length === 0 && <li className="th-column__empty ds-text-subtle">No one here</li>}
        </ul>
        {column.count > column.candidates.length && (
          <button
            type="button"
            className="th-column__more ds-link"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('stage', column.stage);
              next.delete('view');
              setParams(next, { replace: true });
            }}
          >
            See all {column.count}
          </button>
        )}
      </section>
    );
  };

  // --- Table view ------------------------------------------------------------

  const pages = list ? Math.max(1, Math.ceil(list.total / list.pageSize)) : 1;
  const pageNumbers = Array.from({ length: Math.min(pages, 7) }, (_, i) => {
    const start = Math.min(Math.max(1, page - 3), Math.max(1, pages - 6));
    return start + i;
  });

  return (
    <Page className="th-page">
      <PageHeader
        eyebrow="Academics · Teacher Talent Hub"
        title="Teacher Recruitment"
        subtitle="Find and onboard great music teachers with AI-powered screening and structured interviews."
        actions={
          <>
            <Button variant="ghost" icon={<Settings size={16} />} onClick={() => navigate(`${process.env.PUBLIC_URL}/pages/teacher-hiring/settings`)}>
              Settings
            </Button>
            <Button variant="secondary" icon={<Upload size={16} />} onClick={() => setImporting(true)}>Import CSV</Button>
            <Button icon={<UserPlus size={16} />} onClick={() => setAdding(true)}>Add lead</Button>
          </>
        }
      />

      {stats ? <Kpis stats={stats} /> : <Loader label="Loading numbers…" />}

      <Card
        title="Hiring pipeline"
        subtitle={stats ? `${stats.awaitingReview} candidates awaiting review` : undefined}
        actions={
          <Select
            size="sm"
            aria-label="Period"
            value={String(days)}
            onChange={(e) => setDays(Number(e.target.value))}
            options={[
              { value: '7', label: 'Last 7 days' },
              { value: '30', label: 'Last 30 days' },
              { value: '90', label: 'Last 90 days' },
              { value: '365', label: 'Last 12 months' },
              { value: '0', label: 'All time' },
            ]}
          />
        }
      >
        {stats && <Pipeline stats={stats} onPick={(stage) => setFilter('stage', stage === 'NEW' ? '' : stage)} />}
      </Card>

      <section className="th-candidates">
        <h2 className="th-section-title">
          Candidates {list && view === 'table' && <span className="ds-text-subtle ds-num">({list.total.toLocaleString('en-IN')})</span>}
        </h2>
        <Toolbar>
          <Field label="Search" className="ds-toolbar__grow">
            {(ctl) => <SearchInput {...ctl} placeholder="Name, phone, email or city" value={search} onChange={(e) => setSearch(e.target.value)} />}
          </Field>
          {view === 'table' && (
            <Field label="Stage">
              {(ctl) => (
                <Select {...ctl} value={filters.stage} onChange={(e) => setFilter('stage', e.target.value)}
                  options={[{ value: '', label: 'All stages' }, ...[...FUNNEL, ...PARKED].map((s) => ({ value: s, label: STAGE_LABELS[s] }))]} />
              )}
            </Field>
          )}
          <Field label="Genre">
            {(ctl) => (
              <Select {...ctl} value={filters.genre} onChange={(e) => setFilter('genre', e.target.value)}
                options={[{ value: '', label: 'All genres' }, ...genres.map((g) => ({ value: g.id, label: g.name }))]} />
            )}
          </Field>
          <Field label="Source">
            {(ctl) => (
              <Select {...ctl} value={filters.source} onChange={(e) => setFilter('source', e.target.value)}
                options={[{ value: '', label: 'All sources' }, ...(Object.keys(SOURCE_LABELS) as LeadSource[]).map((s) => ({ value: s, label: SOURCE_LABELS[s] }))]} />
            )}
          </Field>
          <Field label="Owner">
            {(ctl) => (
              <Select {...ctl} value={filters.owner} onChange={(e) => setFilter('owner', e.target.value)}
                options={[{ value: '', label: 'Everyone' }, { value: 'me', label: 'Mine' }, { value: 'none', label: 'Unassigned' }]} />
            )}
          </Field>
          <div className="ds-toolbar__end">
            <SegmentedControl<View>
              label="View"
              value={view}
              onChange={(v) => setFilter('view', v === 'table' ? '' : v)}
              options={[{ value: 'table', label: 'Table' }, { value: 'board', label: 'Board' }]}
            />
          </div>
        </Toolbar>

        {error && <Callout tone="danger" title="Couldn't load candidates">{error}</Callout>}

        {view === 'board' &&
          (columns === null ? (
            loading ? <Loader label="Loading the board…" /> : null
          ) : (
            <>
              <p className="ds-text-subtle th-hint">Drag a card to move it. Cards awaiting review come first; the time is how long they have been in the stage.</p>
              <div className="th-board">{columns.filter((c) => (FUNNEL as readonly string[]).includes(c.stage)).map((c) => renderColumn(c))}</div>
              <div className="th-board th-board--parked">{columns.filter((c) => (PARKED as readonly string[]).includes(c.stage)).map((c) => renderColumn(c, true))}</div>
            </>
          ))}

        {view === 'table' && (
          <Card flush>
            {loading && !list ? (
              <Loader />
            ) : !list || list.candidates.length === 0 ? (
              <EmptyState title="No candidates match" description="Try clearing the search or filters, or add a lead." />
            ) : (
              <>
                <Table stickyHeader className="th-table">
                  <thead>
                    <tr>
                      <th>Name</th><th>Genre</th><th>Source</th><th>Stage</th><th className="ds-num">AI score</th><th>Last activity</th><th>Next action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.candidates.map((c) => {
                      const action = nextAction(c);
                      return (
                        <tr key={c.id} className="th-table__row" onClick={() => setOpen({ id: c.id, tab: 'overview' })}>
                          <td>
                            <div className="th-person">
                              <span className="th-avatar" aria-hidden>{initials(c.name)}</span>
                              <div>
                                <button type="button" className="th-person__name ds-link" onClick={(e) => { e.stopPropagation(); setOpen({ id: c.id, tab: 'overview' }); }}>
                                  {c.name}
                                </button>
                                <div className="ds-text-subtle">{[c.city, c.experienceYears != null ? `${c.experienceYears} yrs` : null].filter(Boolean).join(' · ')}</div>
                              </div>
                            </div>
                          </td>
                          <td>{genresOf(c)}</td>
                          <td>{c.source === 'REFERRAL' && c.referrerTeacherName ? `Referral · ${c.referrerTeacherName}` : SOURCE_LABELS[c.source]}</td>
                          <td>
                            <span className={`th-stage th-stage--${c.stage.toLowerCase()}`}>{STAGE_LABELS[c.stage]}</span>
                            {c.stageStatus === 'AWAITING_REVIEW' && <div className="th-stage__status">Awaiting review</div>}
                          </td>
                          <td className="ds-num"><ScoreBox card={c} /></td>
                          <td className="ds-text-subtle">{ago(c.lastActivityAt)}</td>
                          <td>
                            <div className="th-row-actions">
                              <Button size="sm" variant={action.primary ? 'primary' : 'secondary'} onClick={(e) => { e.stopPropagation(); act(c); }}>
                                {action.label}
                              </Button>
                              <CopyLinkButton candidateId={c.id} name={c.name} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <div className="th-pager">
                  <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">‹</Button>
                  {pageNumbers.map((n) => (
                    <Button key={n} size="sm" variant={n === page ? 'primary' : 'ghost'} onClick={() => setPage(n)} aria-current={n === page ? 'page' : undefined}>
                      {n}
                    </Button>
                  ))}
                  <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} aria-label="Next page">›</Button>
                  <span className="ds-text-subtle th-pager__count">
                    {(list.page - 1) * list.pageSize + 1}–{Math.min(list.page * list.pageSize, list.total)} of {list.total.toLocaleString('en-IN')}
                  </span>
                </div>
              </>
            )}
          </Card>
        )}
      </section>

      <CandidateDrawer
        candidateId={open?.id ?? null}
        initialTab={open?.tab}
        genres={genres}
        meta={meta}
        onClose={() => setOpen(null)}
        onChanged={refresh}
      />
      <AddCandidateDialog
        open={adding}
        genres={genres}
        onClose={() => setAdding(false)}
        onAdded={(id) => {
          refresh();
          setOpen({ id, tab: 'overview' });
        }}
      />
      <ImportCandidatesDialog open={importing} onClose={() => setImporting(false)} onImported={refresh} />
      <MoveDialog
        candidate={move ? { id: move.card.id, name: move.card.name, stage: move.card.stage } : null}
        toStage={move?.to ?? null}
        onClose={() => setMove(null)}
        onMoved={refresh}
      />
    </Page>
  );
};

export default TeacherHiringBoard;
